/**
 * What to tell a CSR when a reservation's booking link never reached the
 * customer (BON-472).
 *
 * THE NOTE HAS TO NAME THE REMEDY FOR THE STATE THE ROW IS IN. A flat "use
 * Resend link" reads fine on a live hold and is a dead end everywhere else: a
 * reservation whose email failed and which nobody paid for reaches its deadline
 * as a matter of course, and the CSR who follows that instruction clicks a
 * greyed-out button and has to reverse-engineer the real answer from a tooltip.
 *
 * Shared by the reservations tab and workshop detail, which differ only in
 * WHERE the resend button is. The remedies for every other stage are identical
 * — that is a fact about the reservation, not about the screen — so they are
 * written once.
 */
import type { ReservationStage } from '../../api/reservations';

import { rotationRefusalReason } from './reservationRotationRules';

/**
 * The remedy for a failed send on a stage that CANNOT be resent, phrased to
 * follow "Email never sent, and …". `null` means resend is available and the
 * caller supplies its own pointer at the button.
 */
function deadEndRemedy(stage: ReservationStage): string | null {
  switch (stage) {
    case 'link_sent':
    case 'engaged':
    case 'card_declined':
      return null;
    case 'expired':
      return 'this hold has now lapsed — create a new reservation.';
    case 'attempts_exhausted':
      return 'the customer ran out of payment attempts — create a new reservation.';
    case 'released':
      return 'these seats have already been given back — create a new reservation.';
    case 'paying':
      // Rare but reachable: an activation can outlive a failed send when the
      // customer was given the link another way.
      return 'a payment is in progress — wait for it to finish before sending anything.';
    case 'needs_review':
      return 'this reservation is being looked at by someone — leave it until they are done.';
  }
}

/**
 * The whole note, for a surface where Resend is on this very row.
 *
 * @param resendIsOnThisRow `false` on a read-only surface, where the note has to
 * say where the button lives — and must not send anyone to another screen to
 * find it greyed out.
 */
export function sendFailedNote(stage: ReservationStage, resendIsOnThisRow: boolean): string {
  const deadEnd = deadEndRemedy(stage);
  if (deadEnd) return `Email never sent, and ${deadEnd}`;
  return resendIsOnThisRow
    ? 'Email never sent — use Resend link.'
    : 'Email never sent — resend it from the Reservations tab.';
}

/** Whether the note points at a button the CSR can actually press. Exported for
 *  the test that pins it against the rotation rules: a stage that offers resend
 *  here and refuses it there is the exact defect this module removes. */
export function sendFailureIsRecoverable(stage: ReservationStage): boolean {
  return rotationRefusalReason(stage) === null;
}
